// frontend/packages/business-console/src/pages/Login.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import { getApiScopes, DEFAULT_API_SCOPES } from "../auth/msalConfig";
import { authApi } from "@upryzze/services";
import { useAuth } from "../contexts/AuthContext";
import { getSlugByBusinessId } from "../services/businessService";
import { getBuildStatus } from "../services/buildFunnelService";

const isProduction = process.env.NODE_ENV === "production";

// UpRyzze brand gradient (kept in sync with theme.js brandGradient).
const BRAND_GRADIENT =
  "linear-gradient(120deg, #2563eb 0%, #7c3aed 55%, #ab2ddd 100%)";

const devWarn = (...args) => {
  if (isProduction) return;
  // eslint-disable-next-line no-console
  console.warn(...args);
};

const resolveBusinessId = (me) => {
  if (!me) return null;

  if (me.activeBusinessId) return me.activeBusinessId;

  const access = Array.isArray(me.businessAccess) ? me.businessAccess : [];

  if (access.length === 1) return access[0].businessId;
  if (access.length === 0) return null;

  throw new Error(
    "Multiple businesses are associated with this account. Use a different email or contact support.",
  );
};

function extractAxiosErrorMessage(err) {
  const status = err?.response?.status;
  const data = err?.response?.data;
  const msg =
    data?.message ||
    data?.error ||
    data?.body ||
    err?.message ||
    "Invite could not be accepted.";

  if (status) return `${msg} (HTTP ${status})`;
  return msg;
}

// Translate raw MSAL / auth errors into user-facing copy. We keep the
// original error available on the console for support/dev triage but
// never surface MSAL error codes (e.g.
// `uninitialized_public_client_application`,
// `interaction_required`) directly to end users — they look alarming
// to a customer who doesn't know what MSAL is.
function friendlyAuthMessage(err) {
  const code = String(err?.errorCode || "").toLowerCase();
  const raw = String(err?.message || "").toLowerCase();

  if (code === "interaction_required" || code === "login_required") {
    return "Your session has expired. Please sign in again.";
  }
  if (code === "user_cancelled") {
    return "Sign-in was cancelled.";
  }
  if (
    code === "uninitialized_public_client_application" ||
    raw.includes("uninitialized_public_client_application")
  ) {
    return "Sign-in is starting up. Please try again in a moment.";
  }
  if (code === "consent_required") {
    return "Additional permissions are required. Please sign in again to grant access.";
  }
  if (code === "no_account_error" || raw.includes("no active account")) {
    return "No active session found. Please sign in.";
  }
  if (raw.includes("network") || code === "no_network_connectivity") {
    return "Network error. Please check your connection and try again.";
  }
  return "Sign-in failed. Please try again.";
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance: msalInstance } = useMsal();
  const { refreshUser } = useAuth();

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  // An invited customer arriving from the build funnel has NO account yet, so
  // we send them to the External ID "Create account" screen instead of sign-in.
  const [hasInvitation, setHasInvitation] = useState(false);

  const handledRef = useRef(false);
  const scopesRef = useRef(DEFAULT_API_SCOPES);

  // Persist invitation token across MSAL redirects (B2C can drop query string)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const invitation = params.get("invitation");
      if (invitation) {
        sessionStorage.setItem("pendingInvitation", invitation);
      }
      const pending = invitation || sessionStorage.getItem("pendingInvitation");
      setHasInvitation(Boolean(pending));
    } catch (e) {
      devWarn("Login: failed to persist invitation token", e);
    }
  }, []);

  const postLoginPath = useMemo(() => {
    const from = location?.state?.from?.pathname;
    if (typeof from === "string" && from.startsWith("/business/")) return from;
    return null;
  }, [location?.state?.from?.pathname]);

  useEffect(() => {
    let alive = true;

    getApiScopes()
      .then((scopes) => {
        if (!alive) return;
        if (Array.isArray(scopes) && scopes.length) scopesRef.current = scopes;
      })
      .catch((err) => {
        devWarn("Login: failed to load API scopes; using defaults", err);
        if (!alive) return;
        scopesRef.current = DEFAULT_API_SCOPES;
      });

    return () => {
      alive = false;
    };
  }, []);

  const acceptInviteIfPresent = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get("invitation");

    let fromStorage = null;
    try {
      fromStorage = sessionStorage.getItem("pendingInvitation");
    } catch (e) {
      devWarn("Login: failed to read pendingInvitation", e);
    }

    const invitationToken = fromQuery || fromStorage;
    if (!invitationToken) return false;

    try {
      await authApi.post("/api/invites/accept", { token: invitationToken });

      // clear token everywhere so refresh doesn't re-accept
      try {
        sessionStorage.removeItem("pendingInvitation");
      } catch (e) {
        devWarn("Login: failed to clear pendingInvitation", e);
      }

      params.delete("invitation");
      const newUrl =
        window.location.pathname + (params.toString() ? `?${params.toString()}` : "");
      window.history.replaceState({}, "", newUrl);

      return true;
    } catch (err) {
      // DO NOT swallow this. This is the core of your onboarding flow.
      const msg = extractAxiosErrorMessage(err);
      throw new Error(`Invite acceptance failed: ${msg}`);
    }
  }, []);

  const finishLogin = useCallback(
    async (account) => {
      if (!msalInstance) throw new Error("Authentication client unavailable.");
      if (!account) throw new Error("No active account found. Please sign in again.");

      const scopes = scopesRef.current?.length ? scopesRef.current : DEFAULT_API_SCOPES;

      let tokenResp = null;
      try {
        tokenResp = await msalInstance.acquireTokenSilent({ account, scopes });
      } catch {
        tokenResp = await msalInstance.acquireTokenPopup({ account, scopes });
      }

      if (!tokenResp?.accessToken) {
        throw new Error("Could not acquire access token.");
      }

      sessionStorage.setItem("token", tokenResp.accessToken);

      // Accept invite first (if present). If it fails, stop here with a real error.
      await acceptInviteIfPresent();

      const { data: me } = await authApi.get("/api/auth/me");

      const businessId = resolveBusinessId(me);
      if (!businessId) {
        throw new Error(
          "Your account is not associated with a business yet. If you just accepted an invite, contact support.",
        );
      }

      const snapshot = { ...(me || {}), activeBusinessId: businessId };
      sessionStorage.setItem("user", JSON.stringify(snapshot));
      sessionStorage.setItem("activeBusinessId", String(businessId));

      await refreshUser?.();

      const { data } = await getSlugByBusinessId(businessId);
      const slug = data?.slug;
      if (!slug) throw new Error("Missing business slug.");

      // Land a customer with an active build straight in the guided build
      // flow (interview -> review -> go-live) instead of the operator Home.
      // Only when there's no explicit deep-link target. Degrade-open: any
      // failure (or no active build) falls through to Home.
      let buildTarget = null;
      if (!postLoginPath) {
        try {
          const status = await getBuildStatus();
          if (status?.hasActiveBuild && status?.routeSuffix) {
            buildTarget = `/business/${slug}/${status.routeSuffix}`;
          }
        } catch (e) {
          devWarn("Login: build status check failed; landing on home", e);
        }
      }

      const target = buildTarget || postLoginPath || `/business/${slug}/home`;
      navigate(target, { replace: true, state: { from: location } });
    },
    [acceptInviteIfPresent, location, msalInstance, navigate, postLoginPath, refreshUser],
  );

  const handleMsalLogin = useCallback(async () => {
    setErrorMsg("");
    setLoading(true);

    try {
      if (!msalInstance) throw new Error("Authentication client unavailable.");

      const request = {
        scopes: scopesRef.current?.length
          ? scopesRef.current
          : DEFAULT_API_SCOPES,
      };
      // Invited customers don't have an account yet. prompt=create sends them
      // straight to the External ID "Create account" page instead of the
      // sign-in page, which would dead-end on "we couldn't find an account."
      // They can still switch to "Sign in instead" on that page if needed.
      if (hasInvitation) request.prompt = "create";

      await msalInstance.loginRedirect(request);
    } catch (err) {
      devWarn("Login: loginRedirect failed", err);
      setErrorMsg(friendlyAuthMessage(err));
      setLoading(false);
    }
  }, [msalInstance, hasInvitation]);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    (async () => {
      if (!msalInstance) return;

      try {
        setLoading(true);
        setErrorMsg("");

        const result = await msalInstance.handleRedirectPromise().catch(() => null);
        if (result?.account) {
          msalInstance.setActiveAccount(result.account);
        }

        const account =
          msalInstance.getActiveAccount() || (msalInstance.getAllAccounts()?.[0] ?? null);

        if (!account) return;

        await finishLogin(account);
      } catch (err) {
        try {
          sessionStorage.removeItem("token");
          sessionStorage.removeItem("user");
          sessionStorage.removeItem("activeBusinessId");
        } catch (e) {
          devWarn("Login: failed to clear session storage", e);
        }

        devWarn("Login: redirect-handling failed", err);
        setErrorMsg(friendlyAuthMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [finishLogin, msalInstance]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0f172a 0%, #111827 45%, #1f2937 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Container maxWidth="xs" disableGutters>
        <Paper
          elevation={10}
          sx={{
            p: 4,
            borderRadius: 2,
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography
              component="div"
              sx={{
                fontWeight: 900,
                fontSize: 30,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                mb: 1.25,
                backgroundImage: BRAND_GRADIENT,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
              }}
            >
              UpRyzze
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              UpRyzze Console
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.75 }}>
              {hasInvitation
                ? "Welcome! Create your account to access your new Console."
                : "Sign in to manage your website and account."}
            </Typography>
          </Box>

          {errorMsg ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          ) : null}

          <Button
            variant="contained"
            fullWidth
            onClick={handleMsalLogin}
            disabled={loading}
            sx={{
              py: 1.4,
              fontWeight: 800,
              backgroundImage: BRAND_GRADIENT,
              "&:hover": {
                backgroundImage:
                  "linear-gradient(120deg, #1e40af 0%, #6d28d9 55%, #9333ea 100%)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={22} />
            ) : hasInvitation ? (
              "Create your account"
            ) : (
              "Sign in"
            )}
          </Button>

          <Typography variant="body2" sx={{ mt: 2, textAlign: "center", opacity: 0.75 }}>
            {hasInvitation
              ? "Use the email you signed up with. We will send a verification code to confirm it is you."
              : "Use the email address you were invited with."}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
